import { Card, Space, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import cn from 'classnames';
import React from 'react';

import type { Color } from 'commonTypes/api/scenarios/BaseTypes';

import { BadgeList } from 'shared/ui/BadgeList';
import { BankTitle } from 'shared/ui/BankTitle';
import { OfferCardActiveWrapper } from 'shared/ui/OfferCardActiveWrapper';

import styles from './OfferCard.module.scss';

interface IOfferCardProps {
  title: string;
  subtitle: string;
  logo: string;
  details: Array<{ label: string; value: string }>;
  button: React.ReactNode;
  info?: React.ReactNode;
  containerProps?: React.ComponentProps<typeof Space>;
  active?: boolean;
  activeTitle?: string;
  badges?: Array<{ text: string | JSX.Element; variant: 'primary' | 'secondary'; color: Color }>;
  containerSummaries?: React.ComponentProps<typeof Space>;
  cardProps?: React.ComponentProps<typeof Card>;
  className?: string;
  color?: 'green' | 'orange' | 'blue' | 'red' | 'dark' | 'light' | 'violet';
}

export const OfferCard = ({
  title,
  subtitle,
  logo,
  details,
  button,
  info,
  containerProps = {},
  active,
  activeTitle,
  badges,
  containerSummaries,
  cardProps,
}: IOfferCardProps) => {
  const isMobile = useIsMobile();

  return (
    <OfferCardActiveWrapper
      active={active}
      title={activeTitle}
    >
      <Card
        size={isMobile ? 16 : 24}
        {...cardProps}
        className={cn(styles.card, { [styles.gradientBorder]: active })}
      >
        <Space
          direction="vertical"
          size={16}
        >
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            size={isMobile ? 12 : 32}
            {...containerProps}
          >
            <BankTitle
              logo={logo}
              title={title}
              subtitle={subtitle}
              button={isMobile && button}
            />
            <Space
              justify="end"
              className={styles.summaries}
              size={32}
              {...containerSummaries}
            >
              {details?.map((detail) => {
                return (
                  <Space
                    size={4}
                    direction="vertical"
                    key={detail.label}
                  >
                    <Typography.Text
                      className={cn(styles.detailText, 'h-color-D60')}
                      size={12}
                    >
                      {detail.label}
                    </Typography.Text>

                    <Typography.Text
                      className={styles.detailText}
                      strong
                      size={16}
                    >
                      {detail.value}
                    </Typography.Text>
                  </Space>
                );
              })}
            </Space>

            {!isMobile && button}
          </Space>

          <BadgeList badges={badges} />

          {info}
        </Space>
      </Card>
    </OfferCardActiveWrapper>
  );
};
